import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, Loader2, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Panel, Empty } from "@/components/Bits";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const Thumb = ({ os, foto, canDelete, onDeleted, onOpen }) => {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    api.get(`/ordens/${os.os_id}/fotos/${foto.foto_id}`, { responseType: "blob" })
      .then(({ data }) => { url = URL.createObjectURL(data); setSrc(url); }).catch(() => {});
    return () => url && URL.revokeObjectURL(url);
  }, [os.os_id, foto.foto_id]);
  const del = async () => {
    try { await api.delete(`/ordens/${os.os_id}/fotos/${foto.foto_id}`); onDeleted(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="relative aspect-square rounded-2xl overflow-hidden bg-[#F4F1EA] border border-[#E2DDD3]" data-testid={`os-foto-${foto.foto_id}`}>
      {src ? <img src={src} alt={foto.nome || "Foto"} onClick={() => onOpen(src)} className="h-full w-full object-cover cursor-zoom-in" /> : <Loader2 className="h-6 w-6 animate-spin m-auto absolute inset-0 text-[#1E4D2B]" />}
      {canDelete && <button onClick={del} aria-label="Remover foto" data-testid={`os-foto-delete-${foto.foto_id}`}
        className="absolute top-2 right-2 h-11 w-11 rounded-xl bg-white/90 text-[#B05A40] flex items-center justify-center"><Trash2 className="h-5 w-5" /></button>}
    </div>
  );
};

export const OSPhotos = ({ os, onChange }) => {
  const { user } = useAuth();
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState(null);
  const upload = async (e) => {
    const files = [...(e.target.files || [])];
    e.target.value = "";
    if (!files.length) return;
    setBusy(true);
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      try { await api.post(`/ordens/${os.os_id}/fotos`, fd); } catch (err) { toast.error(errMsg(err)); }
    }
    setBusy(false);
    toast.success("Fotos enviadas");
    onChange();
  };
  return (
    <Panel title={`Fotos (${os.fotos.length})`} testId="os-photos" action={os.pode_editar && (
      <button onClick={() => ref.current.click()} disabled={busy} data-testid="os-foto-add"
        className="h-12 px-4 rounded-xl bg-[#C86D51] text-white font-semibold flex items-center gap-2 hover:bg-[#B05A40] disabled:opacity-60">
        {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Camera className="h-5 w-5" />} Adicionar
      </button>)}>
      <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" onChange={upload} data-testid="os-foto-input" />
      {os.fotos.length === 0 && <Empty>Nenhuma foto registrada.</Empty>}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {os.fotos.map((f) => <Thumb key={f.foto_id} os={os} foto={f} canDelete={os.pode_editar && (user.role === "admin" || f.user_id === user.user_id)} onDeleted={onChange} onOpen={setView} />)}
      </div>
      <Dialog open={!!view} onOpenChange={(o) => !o && setView(null)}>
        <DialogContent className="max-w-4xl p-2 rounded-2xl"><DialogTitle className="sr-only">Foto</DialogTitle>{view && <img src={view} alt="Foto ampliada" className="w-full max-h-[80vh] object-contain rounded-xl" />}</DialogContent>
      </Dialog>
    </Panel>
  );
};
